import * as React from 'react'
import clx from 'classnames'
import { useNavigate } from 'react-router-dom'
import { useAuth } from 'react-oidc-context'

import { SidebarMenuBtn } from '../components/SidebarMenuBtn'
import { SidebarMenu } from '../components/SidebarMenu'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import {
  faArrowRightFromBracketRegular,
  faArrowRightFromBracketSolid,
  faSitemap,
  faCoins,
  faCoinsRegular,
  faFileInvoice,
  faSquareArrowLeftSolid,
  FaIcon,
} from '@/lib/fa'
import { ResponsiveLogo } from '@/app/features/icons/ResponsiveLogo'
import styles from './style.module.css'
import { useSidebarStore } from '@/modules/shared/stores/sidebarStore'
import { useShallow } from 'zustand/shallow'
import { useQueryTenantMe } from '@/modules/subscription/api/useQueryTenantMe'
import { useQueryCompanies } from '../query-client'
import { CompanyIcon } from '@/app/features/icons/Company'
import {
  CompanyType,
  isUnusableServiceCompany,
} from '@/app/constants/companyType'
import { DropdownSideBar } from '@/app/features/company-selection/DropdownSideBar'
import { HOME_ROUTES } from '@/modules/home/routes-typed'

const ServiceCompanyIcon: React.FC<{ className?: string }> = ({ className }) => (
  <FaIcon icon={faSitemap} className={className} />
)

// Sidebar variant for users without an active company / role context — used by
// the subscription onboarding pages (/post-register, /checkout, /subscription/*).
// Strips out every company-scoped nav button (Dashboard, Drivers, etc.) but
// keeps the logo, language switcher and logout so the user can still navigate
// out of a blocked state.
export const AppSidebarMinimal: React.FC = () => {
  const { collapsed, toggle: toggleCollapsed } = useSidebarStore(
    useShallow((s) => ({ collapsed: s.collapsed, toggle: s.toggle })),
  )
  const { t, tSafe, languages, setActiveLanguage, activeLanguage } =
    useLocalize()
  const navigate = useNavigate()
  const auth = useAuth()
  const { data: tenant } = useQueryTenantMe()
  // No tenant yet (just registered) or tenant exists but hasn't paid → user is
  // still in the checkout flow, so surface a sidebar shortcut back to the plan
  // picker. Otherwise /profile becomes a one-way trip and the user is stuck.
  const showCheckoutLink = !tenant || !tenant.paid
  const checkoutHref = tenant ? '/checkout' : '/post-register'

  // Same source as the full auth sidebar — if the user already belongs to a
  // company but landed on the unpaid/checkout screen, give them a picker to
  // jump back into it instead of being stranded in the onboarding flow.
  const companiesQuery = useQueryCompanies({ includeDeleted: false })
  const companiesOptions = companiesQuery.data?.length
    ? companiesQuery.data.map((company) => {
        const name = company.invoiceCompanyName || company.companyName || ''
        const isService = company.type === CompanyType.SERVICE
        return {
          label: name,
          content: isService
            ? `${name} · ${tSafe('serviceAccount.title', 'Konto usługowe')}`
            : undefined,
          value: company.id,
          icon: isService ? ServiceCompanyIcon : CompanyIcon,
        }
      })
    : []

  const handleLogout = async () => {
    try {
      await auth.signoutRedirect()
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Logout error:', err)
    }
  }

  const sidebarWidthClass = clx(
    'tw-w-[54px]',
    !collapsed && 'md:tw-w-[220px] 2xl:tw-w-[264px]',
  )

  return (
    <div
      data-test-id="app-drawer-minimal"
      className={`layer-drawer tw-relative tw-z-[290] tw-m-1 tw-flex tw-h-[calc(100vh-8px)] tw-max-h-[calc(100vh-8px)] tw-flex-col tw-items-stretch tw-justify-between tw-overflow-x-visible tw-rounded-lg tw-border tw-border-base-300 tw-bg-base-100 ${sidebarWidthClass}`}
    >
      <button
        type="button"
        onClick={toggleCollapsed}
        className={clx(
          styles.iconToggleSidebar,
          'tw-hidden md:tw-flex',
          'tw-absolute tw-top-[16px] tw-z-[100]',
          'tw-h-7 tw-w-7 tw-items-center tw-justify-center',
          'tw-rounded-full tw-bg-base-100 tw-shadow',
          'tw-transition hover:tw-bg-[#E4E9F7]',
          collapsed ? 'tw-right-[-15px]' : 'tw-right-[-10px]',
        )}
      >
        <FaIcon
          icon={faSquareArrowLeftSolid}
          className={clx(
            'tw-h-4 tw-w-4 tw-text-content-base tw-transition-transform',
            collapsed && 'tw-rotate-180',
          )}
        />
      </button>
      <div className="custom-scrollbar tw-relative tw-flex-1 tw-overflow-y-auto">
        <div className="tw-flex tw-max-w-[150px] tw-items-center tw-justify-center tw-px-3 tw-pt-5">
          <ResponsiveLogo collapsed={collapsed} />
        </div>
        <SidebarMenu className="tw-mt-6 tw-flex tw-w-full tw-flex-col">
          {companiesOptions.length > 0 && (
            <DropdownSideBar
              collapsed={collapsed}
              className="tw-z-10 tw-text-base-content"
              icon={CompanyIcon}
              placeholder={t('shared.sidebar.select-company')}
              tooltip={t('shared.sidebar.company-selection')}
              options={companiesOptions}
              name="company"
              disabled={companiesQuery.isLoading}
              hideArrow={companiesOptions.length <= 1}
              value={null}
              onChange={(newCompanyId: string) => {
                const target = companiesQuery.data?.find(
                  (c) => String(c.id) === String(newCompanyId),
                )
                // The service company has no database, so its dashboard 500s.
                if (isUnusableServiceCompany(target)) {
                  navigate(`/et/company/${newCompanyId}/serviceAccount`)
                  return
                }
                navigate(
                  HOME_ROUTES.dashboard.buildPath({ companyId: newCompanyId }),
                )
              }}
            />
          )}
          {showCheckoutLink && (
            <SidebarMenuBtn
              solidIcon={faCoins}
              regularIcon={faCoinsRegular}
              href={checkoutHref}
              label="Wybierz plan"
              collapsed={collapsed}
              activeMatchPattern="^/(checkout|post-register)$"
            />
          )}
          <SidebarMenuBtn
            solidIcon={faFileInvoice}
            regularIcon={faFileInvoice}
            href="/profile"
            label="Moja subskrypcja"
            collapsed={collapsed}
            activeMatchPattern="^/profile$"
          />
        </SidebarMenu>
      </div>
      <div className="tw-pb-2">
        <div
          className={clx(
            'tw-flex tw-items-center tw-justify-center tw-gap-1 tw-px-2 tw-py-1.5',
            collapsed
              ? 'tw-flex-col tw-gap-1.5'
              : 'md:tw-flex-row md:tw-justify-start',
          )}
        >
          {languages
            .filter((lang) => ['pl', 'en', 'es', 'ua'].includes(lang.code))
            .map((lang) => (
              <button
                key={lang.code}
                type="button"
                onClick={() => {
                  setActiveLanguage(lang.code)
                  document.cookie = `lang=${lang.code};path=/;max-age=31536000`
                }}
                className={clx(
                  'tw-rounded tw-p-0.5 tw-transition-opacity',
                  activeLanguage?.code === lang.code
                    ? 'tw-opacity-100'
                    : 'tw-opacity-40 hover:tw-opacity-75',
                )}
                title={lang.name}
              >
                <img
                  src={`/assets/img/flags/${lang.code.toUpperCase()}.png`}
                  alt={lang.name}
                  className="tw-h-3 tw-w-5 tw-object-cover"
                />
              </button>
            ))}
        </div>

        <SidebarMenuBtn
          solidIcon={faArrowRightFromBracketSolid}
          regularIcon={faArrowRightFromBracketRegular}
          label="Logout"
          onClick={handleLogout}
          collapsed={collapsed}
          logout
        />
      </div>
    </div>
  )
}
